import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Home, Search, BellRing, Phone, ArrowUpRight, Loader2, X, Send } from "lucide-react";
import { getBio, createSavedSearch } from "../api";
import { useToast } from "../hooks/use-toast";

const ICONS = { valuation: Home, find: Search, alerts: BellRing, contact: Phone };
const gbp = (n) => (n ? `\u00A3${Number(n).toLocaleString()}` : "");

const AlertModal = ({ onClose }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ name: "", email: "", area: "", lookingFor: "buy" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast({ title: "Name and email required" }); return; }
    setBusy(true);
    try { await createSavedSearch(f); toast({ title: "You're registered", description: "We'll alert you when matching homes list." }); onClose(); }
    catch { toast({ title: "Something went wrong" }); }
    finally { setBusy(false); }
  };
  const cls = "w-full h-[48px] px-4 rounded-xl border border-white/20 bg-white/10 text-white placeholder-white/40 text-[14px] outline-none focus:border-[#d9b98a]";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="bio-alert-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-sm bg-[#1c1c1e] rounded-2xl p-6 space-y-3 border border-white/10">
        <div className="flex items-center justify-between"><h3 className="font-display text-[20px] text-white">Register for alerts</h3><button type="button" onClick={onClose}><X size={20} className="text-white/60" /></button></div>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className={cls} data-testid="bio-alert-name" />
        <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email address" className={cls} data-testid="bio-alert-email" />
        <input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} placeholder="Area / postcode (e.g. Bath, BA2)" className={cls} />
        <button type="submit" disabled={busy} className="w-full h-[50px] rounded-xl bg-[#b8935f] text-white font-500 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="bio-alert-submit">{busy ? <Loader2 size={17} className="animate-spin" /> : <><Send size={16} /> Notify me</>}</button>
      </form>
    </div>
  );
};

const BioPage = () => {
  const [bio, setBio] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);

  useEffect(() => { getBio().then(setBio).catch(() => setBio(null)); document.title = "Anadear Estates — Links"; }, []);

  if (!bio) return <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center"><Loader2 className="animate-spin text-[#b8935f]" size={24} /></div>;

  const btnAction = (key) => {
    if (key === "alerts") return { onClick: () => setAlertOpen(true) };
    if (key === "contact") return { href: `mailto:${bio.contactEmail || ""}` };
    if (key === "valuation") return { to: "/#valuation" };
    if (key === "find") return { to: "/buying" };
    return {};
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1c1c1e] via-[#232320] to-[#1c1c1e] py-10 px-4" data-testid="bio-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto rounded-full bg-white/5 border border-[#b8935f]/40 flex items-center justify-center mb-4">
            <svg width="44" height="32" viewBox="0 0 30 22" fill="none"><path d="M2 20 C2 9 8 3 15 3 C22 3 28 9 28 20" stroke="#b8935f" strokeWidth="1.8" fill="none" strokeLinecap="round" /><path d="M7 20 C7 12 10 8 15 8 C20 8 23 12 23 20" stroke="#b8935f" strokeWidth="1.3" fill="none" strokeLinecap="round" opacity="0.7" /><line x1="15" y1="1" x2="15" y2="6" stroke="#b8935f" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
          <h1 className="font-display text-white text-[26px] font-400" data-testid="bio-headline">{bio.headline}</h1>
          <p className="text-white/50 text-[14px] mt-1.5">{bio.subheadline}</p>
        </div>

        <div className="space-y-3 mb-8" data-testid="bio-buttons">
          {(bio.buttons || []).map((b) => {
            const Icon = ICONS[b.key] || ArrowUpRight;
            const a = btnAction(b.key);
            const inner = (<><span className="flex items-center gap-3"><Icon size={18} className="text-[#d9b98a]" /> {b.label}</span><ArrowUpRight size={16} className="text-white/40 group-hover:text-white transition-colors" /></>);
            const cls = "group w-full flex items-center justify-between px-5 h-[58px] rounded-2xl bg-white/[0.07] border border-white/10 text-white text-[15px] font-500 hover:bg-white/[0.12] hover:border-[#b8935f]/50 transition-all";
            if (a.to) return <Link key={b.key} to={a.to} className={cls} data-testid={`bio-btn-${b.key}`}>{inner}</Link>;
            if (a.href) return <a key={b.key} href={a.href} className={cls} data-testid={`bio-btn-${b.key}`}>{inner}</a>;
            return <button key={b.key} className={cls} data-testid={`bio-btn-${b.key}`} {...a}>{inner}</button>;
          })}
          {(bio.customLinks || []).map((l, i) => (
            <a key={`c${i}`} href={l.url} target="_blank" rel="noreferrer" className="group w-full flex items-center justify-between px-5 h-[58px] rounded-2xl bg-white/[0.07] border border-white/10 text-white text-[15px] font-500 hover:bg-white/[0.12] transition-all" data-testid={`bio-custom-${i}`}>
              <span>{l.title}</span><ArrowUpRight size={16} className="text-white/40" />
            </a>
          ))}
        </div>

        {(bio.featured || []).length > 0 && (
          <div className="mb-8">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#d9b98a] mb-3 text-center">Featured properties</p>
            <div className="grid grid-cols-2 gap-3" data-testid="bio-featured">
              {bio.featured.map((p) => (
                <Link key={p.slug} to={`/property/${p.slug}`} className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-[#b8935f]/50 transition-all" data-testid={`bio-property-${p.slug}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-white/5">
                    {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                    {p.qualifier && <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-black/70 text-[#d9b98a]">{p.qualifier}</span>}
                  </div>
                  <div className="p-3">
                    <p className="text-white text-[13px] font-500 leading-tight line-clamp-1">{p.title}</p>
                    <p className="text-[#d9b98a] text-[13px] mt-1">{gbp(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-4">
          {bio.socials?.instagram && <a href={bio.socials.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#E1306C] transition-colors"><Instagram size={19} /></a>}
          {bio.socials?.linkedin && <a href={bio.socials.linkedin} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#0A66C2] transition-colors"><Linkedin size={19} /></a>}
          {bio.socials?.facebook && <a href={bio.socials.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white/[0.07] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#1877F2] transition-colors"><Facebook size={19} /></a>}
        </div>
        <p className="text-center text-white/30 text-[11px] mt-8">© {new Date().getFullYear()} {bio.headline}</p>
      </div>
      {alertOpen && <AlertModal onClose={() => setAlertOpen(false)} />}
    </div>
  );
};

export default BioPage;
